/**
 * pastoralPatternRecognition.js
 *
 * Detects longitudinal pastoral patterns from the spiritual journey history.
 * Reads from spiritualJourneyMemory (60-day window) — NOT from relationalMemoryEngine.
 *
 * DISTINCT from predictivePastoralSignals.js (short-term, visit-based signals).
 * This engine operates on WEEKS/MONTHS of spiritual process states.
 *
 * Patterns:
 *   ciclo_crisis             — repeating crisis → brief recovery → crisis again
 *   mejora_sostenida         — sustained upward movement over multiple sessions
 *   aislamiento_progresivo   — steady deepening of distance or exhaustion
 *   restauracion_gradual     — slow, consistent movement toward restoration
 *   oscilacion_emocional     — back-and-forth between states without resolution
 *   busqueda_creciente       — increasing spiritual seeking over time
 *   agotamiento_persistente  — spiritual exhaustion present across multiple sessions
 *   patron_culpa             — recurring shame/guilt state over time
 *   hiperexigencia           — perfectionist spiritual pressure pattern
 *   sin_patron               — insufficient data or no clear pattern
 */

import { getRecentEntries, getJourneySpanDays } from "./spiritualJourneyMemory";

const CRISIS_STATES       = new Set(["quebranto", "resistencia", "confusión"]);
const RECOVERY_STATES     = new Set(["restauración", "apertura", "crecimiento"]);
const EXHAUSTION_STATES   = new Set(["agotamiento_espiritual"]);
const SEEKING_STATES      = new Set(["búsqueda"]);
const SHAME_STATES        = new Set(["quebranto"]);  // quebranto when paired with culpa tone

/**
 * @param {import("./spiritualJourneyMemory").JourneyRecord|null} record
 * @returns {{
 *   pattern:     string,
 *   confidence:  "low"|"medium"|"high",
 *   description: string|null,
 *   spanDays:    number,
 * }}
 */
export function detectPastoralPattern(record) {
  const empty = { pattern: "sin_patron", confidence: "low", description: null, spanDays: 0 };

  if (!record?.entries?.length || record.entries.length < 3) return empty;

  const entries  = record.entries;  // most recent first
  const spanDays = getJourneySpanDays(record);
  const states   = entries.map((e) => e.processState);

  // ── Agotamiento persistente ────────────────────────────────────────────────
  const exhaustionCount = states.filter((s) => EXHAUSTION_STATES.has(s)).length;
  if (exhaustionCount >= 3) {
    return {
      pattern: "agotamiento_persistente",
      confidence: exhaustionCount >= 4 ? "high" : "medium",
      description: "Señales persistentes de agotamiento espiritual a lo largo del tiempo.",
      spanDays,
    };
  }

  // ── Mejora sostenida ───────────────────────────────────────────────────────
  const recent3  = states.slice(0, 3);
  const allGood  = recent3.every((s) => RECOVERY_STATES.has(s));
  if (allGood && entries.length >= 4) {
    const prev = states.slice(3, 6);
    const hadDifficulty = prev.some((s) => CRISIS_STATES.has(s) || EXHAUSTION_STATES.has(s));
    if (hadDifficulty) {
      return {
        pattern: "mejora_sostenida",
        confidence: "high",
        description: "Hay mejoría real sostenida después de un período difícil.",
        spanDays,
      };
    }
  }

  // ── Restauración gradual ───────────────────────────────────────────────────
  const recoveryCount = states.filter((s) => RECOVERY_STATES.has(s)).length;
  const crisisCount   = states.filter((s) => CRISIS_STATES.has(s)).length;
  if (recoveryCount >= 2 && crisisCount >= 1 && states[0] !== undefined && RECOVERY_STATES.has(states[0])) {
    if (crisisCount < recoveryCount) {
      return {
        pattern: "restauracion_gradual",
        confidence: "medium",
        description: "Proceso de restauración en marcha — avance gradual.",
        spanDays,
      };
    }
  }

  // ── Ciclo de crisis ────────────────────────────────────────────────────────
  // Pattern: crisis → recovery → crisis at least twice
  let cycleCount = 0;
  for (let i = 0; i < states.length - 2; i++) {
    if (CRISIS_STATES.has(states[i + 1]) && RECOVERY_STATES.has(states[i])) cycleCount++;
  }
  if (cycleCount >= 2) {
    return {
      pattern: "ciclo_crisis",
      confidence: cycleCount >= 3 ? "high" : "medium",
      description: "Patrón de ciclos de crisis recurrentes — progreso interrumpido.",
      spanDays,
    };
  }

  // ── Oscilación emocional ───────────────────────────────────────────────────
  let transitions = 0;
  for (let i = 0; i < states.length - 1; i++) {
    if (states[i] !== states[i + 1]) transitions++;
  }
  const oscillationRatio = transitions / Math.max(1, states.length - 1);
  if (oscillationRatio >= 0.7 && entries.length >= 5) {
    return {
      pattern: "oscilacion_emocional",
      confidence: "medium",
      description: "Estado espiritual oscilante — aún sin una dirección clara.",
      spanDays,
    };
  }

  // ── Búsqueda creciente ─────────────────────────────────────────────────────
  const seekingCount = states.filter((s) => SEEKING_STATES.has(s)).length;
  if (seekingCount >= 3 && SEEKING_STATES.has(states[0])) {
    return {
      pattern: "busqueda_creciente",
      confidence: "medium",
      description: "Búsqueda espiritual sostenida y creciente.",
      spanDays,
    };
  }

  // ── Aislamiento progresivo ─────────────────────────────────────────────────
  // All recent states are crisis/exhaustion and trend is worsening
  const allDifficult = recent3.every((s) => CRISIS_STATES.has(s) || EXHAUSTION_STATES.has(s));
  if (allDifficult && entries.length >= 4) {
    return {
      pattern: "aislamiento_progresivo",
      confidence: "medium",
      description: "Señales de aislamiento progresivo — la situación no ha mejorado.",
      spanDays,
    };
  }

  // ── Patrón de culpa ────────────────────────────────────────────────────────
  const shameCount = states.filter((s) => SHAME_STATES.has(s)).length;
  const shameRatio = shameCount / states.length;
  if (shameRatio >= 0.5 && shameCount >= 3) {
    return {
      pattern: "patron_culpa",
      confidence: "medium",
      description: "Presencia recurrente de quebranto profundo — posible carga de culpa.",
      spanDays,
    };
  }

  return empty;
}
